/**
 * Swaps the product gallery to the images of the combination matched by
 * hulk-option-images.js (event `hulk:option-images-matched`), and restores the
 * product's own media when no combination matches.
 *
 * It works with the theme's <media-gallery> (main slider, collage layout,
 * thumbnails and the zoom viewer) without editing it: the original items are
 * hidden and extra items (marked data-hulk-image) are added next to them, then
 * the gallery's `mediaLists` is pointed at the visible set so the theme's own
 * thumbnail / slider / zoom code keeps working.
 *
 * Image URLs are plain CDN URLs, e.g.
 *   https://cdn.shopify.com/s/files/1/1078/8332/files/name.png?v=1787159522
 * Sized variants are produced with a `width` query parameter.
 */
(() => {
  const DEBUG = false;
  const log = (...args) => {
    if (DEBUG && window.hulkOptionImagesDebug !== false) console.log('[hulk-option-images-gallery]', ...args);
  };
  const warn = (...args) => console.warn('[hulk-option-images-gallery]', ...args);

  const MAIN_SIZES = '(min-width: 1480px) 740px, (min-width: 768px) 50vw, 100vw';
  const MAIN_WIDTHS = [480, 740, 980, 1200, 1600];
  const THUMB_WIDTHS = [65, 130];
  const MODAL_THUMB_WIDTH = 138;
  const SKELETON_MIN_MS = 500; // minimum time to show the skeleton placeholder, even for cached images
  const DEFAULT_ZOOM_WIDTH = 2400;

  function sized(url, width) {
    try {
      const sizedUrl = new URL(url, window.location.href);
      sizedUrl.searchParams.set('width', width);
      return sizedUrl.toString();
    } catch {
      return url;
    }
  }

  const srcset = (url, widths) => widths.map((width) => `${sized(url, width)} ${width}w`).join(', ');

  function createImage({ url, widths, sizes, width, loading, className }) {
    const img = document.createElement('img');
    img.src = sized(url, width);
    img.srcset = srcset(url, widths);
    if (sizes) img.sizes = sizes;
    img.alt = '';
    img.loading = loading;
    img.className = className;
    return img;
  }

  function wrapInAspectRatio(img) {
    const box = document.createElement('div');
    box.className = 'img-ar img-ar--contain';
    box.style.setProperty('--aspect-ratio', '1.0');
    box.appendChild(img);
    // Cached image: nothing to wait for, no skeleton.
    if (img.complete) return box;

    // Skeleton placeholder (see hulk-custom.css): shown until the image has loaded
    // and for at least SKELETON_MIN_MS.
    const skeleton = document.createElement('div');
    skeleton.className = 'hulk-skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    box.classList.add('hulk-img-loading');
    box.appendChild(skeleton);
    const shownAt = Date.now();
    const done = () => {
      setTimeout(() => {
        skeleton.remove();
        box.classList.remove('hulk-img-loading');
      }, Math.max(0, SKELETON_MIN_MS - (Date.now() - shownAt)));
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    return box;
  }

  class GalleryImageSwapper {
    constructor(gallery) {
      this.gallery = gallery;
      this.originalLists = null;
      this.originalActiveId = null;
      this.originalSingleImage = false;
      this.currentKey = '';
    }

    get viewer() {
      return this.gallery.galleryModal?.querySelector('gallery-viewer') || null;
    }

    get zoomWidth() {
      const link = this.gallery.querySelector('.show-gallery:not([data-hulk-image])');
      const width = link ? Number(new URL(link.href, window.location.href).searchParams.get('width')) : 0;
      return width || DEFAULT_ZOOM_WIDTH;
    }

    // images: array of URLs, or null/empty to restore the product's own media.
    apply(images) {
      const urls = Array.isArray(images) ? images.filter(Boolean) : [];
      const key = urls.join('|');
      if (key === this.currentKey) {
        log('same images as before, nothing to do');
        return;
      }
      this.currentKey = key;

      if (!this.originalLists) {
        this.originalLists = [...this.gallery.mediaLists];
        this.originalActiveId = this.gallery.querySelector('.main-image .slider__item.is-active')?.dataset.mediaId;
        this.originalSingleImage = Boolean(this.viewer?.classList.contains('gallery-viewer--single-image'));
      }

      this.removeCustomItems();

      if (!urls.length) {
        this.originalLists.forEach((list) => list.forEach((el) => { el.hidden = false; }));
        this.gallery.mediaLists = [...this.originalLists];
        this.viewer?.classList.toggle('gallery-viewer--single-image', this.originalSingleImage);
        this.refreshSliders();
        if (this.originalActiveId) this.gallery.setActiveMedia(this.originalActiveId, true);
        log('restored product media');
        return;
      }

      this.originalLists.forEach((list) => list.forEach((el) => { el.hidden = true; }));
      this.addCustomItems(urls);

      this.gallery.mediaLists = [
        this.gallery.querySelectorAll('.main-image .slider__item[data-hulk-image]'),
        this.gallery.querySelectorAll('.product-media-collage__item[data-hulk-image]'),
        this.gallery.querySelectorAll('.thumbnails .slider__item[data-hulk-image]'),
        ...(this.viewer ? [this.viewer.querySelectorAll('.gallery-viewer__thumb[data-hulk-image]')] : []),
      ];
      this.viewer?.classList.toggle('gallery-viewer--single-image', urls.length === 1);

      this.refreshSliders();
      this.gallery.setActiveMedia('hulk-0', true);
      log('gallery swapped to', urls.length, 'images');
    }

    removeCustomItems() {
      this.gallery.querySelectorAll('[data-hulk-image]').forEach((el) => el.remove());
      this.viewer?.querySelectorAll('[data-hulk-image]').forEach((el) => el.remove());
    }

    refreshSliders() {
      this.gallery.querySelectorAll('carousel-slider').forEach((slider) => {
        if (slider.offsetParent && typeof slider.refresh === 'function') slider.refresh();
      });
    }

    addCustomItems(urls) {
      const zoomEnabled = this.gallery.hasAttribute('data-zoom-enabled');
      const mainGrid = this.gallery.querySelector('.main-image .slider__grid');
      const collage = this.gallery.querySelector('.product-media-collage');
      const thumbGrid = this.gallery.querySelector('.thumbnails .slider__grid');
      const modalThumbs = this.viewer?.querySelector('.gallery-viewer__thumbs');

      urls.forEach((url, index) => {
        const mediaId = `hulk-${index}`;
        const zoomUrl = sized(url, this.zoomWidth);

        if (mainGrid) mainGrid.appendChild(this.buildMainItem(url, zoomUrl, mediaId, index, urls.length, zoomEnabled));
        if (collage) collage.appendChild(this.buildCollageItem(url, zoomUrl, mediaId, index, zoomEnabled));
        if (thumbGrid) thumbGrid.appendChild(this.buildThumbnail(url, zoomUrl, mediaId, index, zoomEnabled));
        if (modalThumbs) modalThumbs.appendChild(this.buildModalThumb(url, zoomUrl, mediaId));
      });
    }

    buildMainImage(url, index) {
      const wrapper = document.createElement('div');
      wrapper.className = 'product-media-wrapper';
      wrapper.dataset.mediaId = `hulk-main-${index}`;
      wrapper.tabIndex = -1;

      const media = document.createElement('div');
      media.className = 'product-media product-media--image';
      media.appendChild(
        wrapInAspectRatio(
          createImage({
            url,
            widths: MAIN_WIDTHS,
            sizes: MAIN_SIZES,
            width: 1200,
            loading: index === 0 ? 'eager' : 'lazy',
            className: 'theme-img',
          })
        )
      );
      wrapper.appendChild(media);
      return wrapper;
    }

    buildMainItem(url, zoomUrl, mediaId, index, total, zoomEnabled) {
      const item = document.createElement('div');
      item.className = `slider__item${index === 0 ? ' is-active' : ''}`;
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'Slide');
      item.setAttribute('aria-label', `${index + 1} of ${total}`);
      if (index !== 0) item.tabIndex = -1;
      item.dataset.mediaId = mediaId;
      item.dataset.hulkImage = 'true';

      const image = this.buildMainImage(url, index);
      if (zoomEnabled) {
        const link = document.createElement('a');
        link.className = 'block show-gallery';
        link.href = zoomUrl;
        link.setAttribute('aria-label', `Load image ${index + 1} in gallery view`);
        link.dataset.hulkImage = 'true';
        link.appendChild(image);
        item.appendChild(link);
      } else {
        item.appendChild(image);
      }
      return item;
    }

    buildCollageItem(url, zoomUrl, mediaId, index, zoomEnabled) {
      const item = document.createElement('div');
      item.className = `product-media-collage__item${index === 0 ? ' is-active' : ''}`;
      item.dataset.mediaId = mediaId;
      item.dataset.hulkImage = 'true';

      const image = this.buildMainImage(url, index);
      if (zoomEnabled) {
        const link = document.createElement('a');
        link.className = 'show-gallery';
        link.href = zoomUrl;
        link.appendChild(image);
        item.appendChild(link);
      } else {
        item.appendChild(image);
      }
      return item;
    }

    buildThumbnail(url, zoomUrl, mediaId, index, zoomEnabled) {
      const item = document.createElement('li');
      item.className = `slider__item${index === 0 ? ' is-active' : ''}`;
      item.dataset.mediaId = mediaId;
      item.dataset.hulkImage = 'true';

      const link = document.createElement('a');
      link.className = 'thumbnail thumbnail--media-image';
      link.href = zoomEnabled ? zoomUrl : '#';
      link.appendChild(
        wrapInAspectRatio(
          createImage({
            url,
            widths: THUMB_WIDTHS,
            sizes: '65px',
            width: 130,
            loading: 'lazy',
            className: 'theme-img',
          })
        )
      );
      item.appendChild(link);
      return item;
    }

    buildModalThumb(url, zoomUrl, mediaId) {
      const link = document.createElement('a');
      link.className = 'gallery-viewer__thumb';
      link.href = '#';
      link.dataset.zoomUrl = zoomUrl;
      link.dataset.mediaId = mediaId;
      link.dataset.hulkImage = 'true';

      const img = document.createElement('img');
      img.className = 'gallery-viewer__thumb-img';
      img.src = sized(url, MODAL_THUMB_WIDTH);
      img.alt = '';
      img.loading = 'lazy';
      link.appendChild(img);
      return link;
    }
  }

  // Warm the browser cache with every mapped image (same src/srcset/sizes as the
  // gallery uses, so the same candidate is picked) so a swap does not wait on the network.
  function preloadMappedImages() {
    let urls = [];
    try {
      const data = JSON.parse(document.getElementById('product-option-images')?.textContent || 'null');
      urls = [...new Set((data?.combinations || []).flatMap((combination) => combination.images || []).filter(Boolean))];
    } catch {
      return;
    }

    const queue = [...urls];
    const keep = [];
    const next = () => {
      const url = queue.shift();
      if (!url) return;
      const img = createImage({ url, widths: MAIN_WIDTHS, sizes: MAIN_SIZES, width: 1200, loading: 'eager', className: '' });
      // Thumbnail size too (same src/srcset/sizes as buildThumbnail).
      keep.push(img, createImage({ url, widths: THUMB_WIDTHS, sizes: '65px', width: 130, loading: 'eager', className: '' }));
      img.addEventListener('load', next, { once: true });
      img.addEventListener('error', next, { once: true });
    };
    // A few in parallel, started when the browser is idle.
    const start = () => { for (let i = 0; i < 3; i += 1) next(); };
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 });
    else setTimeout(start, 1000);
  }

  if (document.readyState === 'complete') preloadMappedImages();
  else window.addEventListener('load', preloadMappedImages);

  const swappers = new WeakMap();

  document.addEventListener('hulk:option-images-matched', (event) => {
    const gallery = event.target.closest?.('.js-product')?.querySelector('media-gallery');
    if (!gallery || !gallery.mediaLists) {
      warn('media-gallery not found / not ready, skipping');
      return;
    }
    if (gallery.mediaGroupingEnabled) {
      warn('media grouping is enabled in this section; image swap is not supported with it');
      return;
    }

    if (!swappers.has(gallery)) swappers.set(gallery, new GalleryImageSwapper(gallery));

    const combination = event.detail?.combination;
    log('combination:', combination ? combination.id : 'none', combination?.images);
    swappers.get(gallery).apply(combination?.images);
  });
})();
